/**
 * PolicyLifecycleDialog — SideSheet form for policy state transitions.
 *
 * Valid transitions (ADR-103 §7):
 *   draft      → shadow | activate
 *   shadow     → activate | deprecate
 *   active     → deprecate
 *   deprecated → archive
 *
 * Reason is required (ECTP Rule 7) and shown in the audit trail.
 * `activate` on a `draft` policy surfaces a warning about the mandatory
 * shadow period for MAJOR policies (§7 shadow-mode zorunlu).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Archive,
  CheckCircle,
  EyeSlash,
  Trash,
} from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button';
import { FieldError } from '../../shared/ui/FieldError';
import { Label } from '../../shared/ui/Label';
import { SideSheet } from '../../shared/ui/SideSheet';
import { Textarea } from '../../shared/ui/Textarea';
import { useApplyPolicyLifecycle } from '../../features/business-policies/useBusinessPolicies';
import {
  policyLifecycleFormSchema,
  type PolicyLifecycleAction,
  type PolicyLifecycleFormValues,
  type PolicyRecord,
} from '../../features/business-policies/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: PolicyRecord;
  action: PolicyLifecycleAction;
}

const ACTION_ICON: Record<PolicyLifecycleAction, JSX.Element> = {
  shadow: <EyeSlash size={18} weight="regular" />,
  activate: <CheckCircle size={18} weight="regular" />,
  deprecate: <Trash size={18} weight="regular" />,
  archive: <Archive size={18} weight="regular" />,
};

export function PolicyLifecycleDialog({
  open,
  onOpenChange,
  record,
  action,
}: Props): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const applyMutation = useApplyPolicyLifecycle(record.policyId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PolicyLifecycleFormValues>({
    resolver: zodResolver(policyLifecycleFormSchema),
    defaultValues: { action, reason: '' },
  });

  useEffect(() => {
    reset({ action, reason: '' });
  }, [action, reset, open]);

  const onSubmit = async (values: PolicyLifecycleFormValues) => {
    try {
      await applyMutation.mutateAsync(values);
      reset();
      onOpenChange(false);
    } catch {
      // silent — mutation surface will show; keep sheet open for correction.
    }
  };

  const isDangerous = action === 'deprecate' || action === 'archive';
  const directActivate = action === 'activate' && record.status === 'draft';

  return (
    <SideSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t(`lifecycle.titles.${action}`, { name: record.name })}
      description={t(`lifecycle.descriptions.${action}`)}
      closeLabel={t('lifecycle.cancel')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-biz-content-lg" noValidate>
        <input type="hidden" {...register('action')} />

        {directActivate ? (
          <div
            role="alert"
            className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-warning-subtle border border-biz-warning/30 text-biz-sm text-biz-warning"
          >
            {t('lifecycle.shadowRecommended')}
          </div>
        ) : null}

        {action === 'deprecate' ? (
          <div
            role="alert"
            className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle border border-biz-danger/30 text-biz-sm text-biz-danger"
          >
            {t('lifecycle.deprecateWarning')}
          </div>
        ) : null}

        {action === 'archive' ? (
          <div
            role="alert"
            className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle border border-biz-danger/30 text-biz-sm text-biz-danger"
          >
            {t('lifecycle.archiveWarning')}
          </div>
        ) : null}

        <div>
          <Label htmlFor="policy-lifecycle-reason">{t('lifecycle.reasonLabel')}</Label>
          <Textarea
            id="policy-lifecycle-reason"
            rows={4}
            placeholder={t('lifecycle.reasonPlaceholder')}
            invalid={Boolean(errors.reason)}
            {...register('reason')}
          />
          <FieldError id="policy-lifecycle-reason-error">
            {errors.reason ? t('lifecycle.reasonRequired') : undefined}
          </FieldError>
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t('lifecycle.reasonHint')}
          </p>
        </div>

        {applyMutation.isError ? (
          <FieldError id="policy-lifecycle-submit-error">
            {applyMutation.error instanceof Error
              ? applyMutation.error.message
              : t('lifecycle.submitError')}
          </FieldError>
        ) : null}

        <div className="flex items-center justify-end gap-biz-content-lg pt-biz-content-lg border-t border-biz-border">
          <Button type="button" intent="secondary" onClick={() => onOpenChange(false)}>
            {t('lifecycle.cancel')}
          </Button>
          <Button
            type="submit"
            intent={isDangerous ? 'danger' : 'primary'}
            leftIcon={ACTION_ICON[action]}
            loading={isSubmitting || applyMutation.isPending}
          >
            {t(`lifecycle.submit.${action}`)}
          </Button>
        </div>
      </form>
    </SideSheet>
  );
}
